import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DELETION_PATH, SUPPORT_EMAIL, SUPPORT_MAILTO, WHATSAPP_DISPLAY, WHATSAPP_URL } from '../config/support';
import { Section, SectionHeading } from './ui';

// The WU-C deck questions, plus what a recipient needs for mobile money and for cash (WU-RECV moved these
// out of "How they get it"). The typical delivery time joins when it is confirmed.
const QUESTIONS: { q: string; a: string; panelId: string }[] = [
  { q: 'faq.q1', a: 'faq.a1', panelId: 'faq-panel-1' },
  { q: 'faq.q2', a: 'faq.a2', panelId: 'faq-panel-2' },
  { q: 'faq.q3', a: 'faq.a3', panelId: 'faq-panel-3' },
  { q: 'faq.q4', a: 'faq.a4', panelId: 'faq-panel-4' },
  { q: 'faq.q5', a: 'faq.a5', panelId: 'faq-panel-5' },
  { q: 'faq.q6', a: 'faq.a6', panelId: 'faq-panel-6' },
  { q: 'faq.q7', a: 'faq.a7', panelId: 'faq-panel-7' },
  { q: 'faq.q8', a: 'faq.a8', panelId: 'faq-panel-8' },
  { q: 'faq.q9', a: 'faq.a9', panelId: 'faq-panel-9' },
];

const COLUMN_BREAK = Math.ceil(QUESTIONS.length / 2);

/** Turns the support email and WhatsApp number in an answer into links (they are verbatim in every locale). */
function linkify(text: string): React.ReactNode[] {
  const pattern = new RegExp(`(${[SUPPORT_EMAIL, WHATSAPP_DISPLAY].map((s) => s.replace(/[.+]/g, '\\$&')).join('|')})`);
  return text.split(pattern).map((part, index) => {
    if (part === SUPPORT_EMAIL) {
      return (
        <a key={index} className="faq-item__link" href={SUPPORT_MAILTO}>
          {part}
        </a>
      );
    }
    if (part === WHATSAPP_DISPLAY) {
      return (
        <a key={index} className="faq-item__link" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

const CommonQuestions: React.FC = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const renderItem = (item: (typeof QUESTIONS)[number], index: number) => {
    const open = openIndex === index;
    return (
      <div key={item.panelId} className={open ? 'faq-item faq-item--open' : 'faq-item'}>
        <button
          type="button"
          className="faq-item__button"
          aria-expanded={open}
          aria-controls={item.panelId}
          onClick={() => setOpenIndex(open ? null : index)}
        >
          <span className="faq-item__question">{t(item.q)}</span>
          <span className="faq-item__toggle" aria-hidden="true" />
        </button>
        <div id={item.panelId} className={open ? 'faq-answer faq-answer--open' : 'faq-answer'}>
          <div className="faq-answer__clip">
            <div className="faq-item__answer">
              <p>{linkify(t(item.a))}</p>
              {item.a === 'faq.a7' ? (
                <p>
                  <Link className="faq-item__link" to={DELETION_PATH}>
                    {t('faq.a7Link')}
                  </Link>
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Section id="faq" surface="paper" aria-label={t('faq.eyebrow')}>
      <SectionHeading eyebrow={t('faq.eyebrow')} title={t('faq.title')} />
      <div className="faq-grid">
        <div className="faq-column">{QUESTIONS.slice(0, COLUMN_BREAK).map((item, i) => renderItem(item, i))}</div>
        <div className="faq-column">
          {QUESTIONS.slice(COLUMN_BREAK).map((item, i) => renderItem(item, i + COLUMN_BREAK))}
        </div>
      </div>
    </Section>
  );
};

export default CommonQuestions;
